import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { checkBookService, CheckBook } from '../../../services/checkBookService';
import { getCheckNotesByCheckBook, CheckNoteDto, CheckNoteStatus } from '../../../services/checkNoteService';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';

// Yaprak durumu
interface LeafInfo {
  leafNumber: number;
  isUsed: boolean;
  checkNote?: CheckNoteDto;
}

// Filtre tipi
type FilterType = 'all' | 'used' | 'unused';

// Durum label/renk mapping
const statusConfig: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
  PORTFOLIO: { label: 'Portföyde', color: 'info' },
  IN_COLLECTION: { label: 'Tahsilatta', color: 'primary' },
  COLLECTED: { label: 'Tahsil Edildi', color: 'success' },
  PARTIAL_COLLECTED: { label: 'Kısmi Tahsil', color: 'warning' },
  ENDORSED: { label: 'Ciro Edildi', color: 'secondary' },
  COLLATERAL: { label: 'Teminata Verildi', color: 'warning' },
  COLLATERAL_RETURNED: { label: 'Teminattan İade', color: 'info' },
  BOUNCED: { label: 'Karşılıksız', color: 'error' },
  RETURNED: { label: 'İade Edildi', color: 'default' },
  CANCELED: { label: 'İptal Edildi', color: 'error' },
  GIVEN_PENDING: { label: 'Beklemede', color: 'warning' },
  GIVEN_PAID: { label: 'Ödendi', color: 'success' },
  GIVEN_RETURNED: { label: 'İade Edildi', color: 'default' },
};

const CheckBookLeaves: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userDetails } = useAuth();

  const [checkBook, setCheckBook] = useState<CheckBook | null>(null);
  const [checkNotes, setCheckNotes] = useState<CheckNoteDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');

  // Veri yükle
  const loadData = async () => {
    if (!userDetails?.companyId || !id) return;

    setLoading(true);
    setError(null);

    try {
      // Çek defteri bilgisi
      const checkBooksData = await checkBookService.getByCompany(userDetails.companyId);
      const currentCheckBook = checkBooksData.find(cb => cb.id === parseInt(id));

      if (!currentCheckBook) {
        setError('Çek defteri bulunamadı');
        setLoading(false);
        return;
      }

      setCheckBook(currentCheckBook);

      // Çek defterine ait kullanılmış yapraklar
      const notes = await getCheckNotesByCheckBook(parseInt(id));
      setCheckNotes(notes);
    } catch (err) {
      console.error('Yapraklar yüklenirken hata:', err);
      setError('Yapraklar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [userDetails?.companyId, id]);

  // Tüm yaprakları oluştur (startNumber → endNumber)
  const allLeaves = useMemo<LeafInfo[]>(() => {
    if (!checkBook) return [];

    const leaves: LeafInfo[] = [];
    for (let num = checkBook.startNumber; num <= checkBook.endNumber; num++) {
      const matchingNote = checkNotes.find(
        cn => cn.checkNumber === num.toString() || (cn as any).checkBookNumber === num
      );
      leaves.push({
        leafNumber: num,
        isUsed: !!matchingNote,
        checkNote: matchingNote
      });
    }
    return leaves;
  }, [checkBook, checkNotes]);

  // Filtrelenmiş yapraklar
  const filteredLeaves = useMemo(() => {
    if (filter === 'used') return allLeaves.filter(l => l.isUsed);
    if (filter === 'unused') return allLeaves.filter(l => !l.isUsed);
    return allLeaves;
  }, [allLeaves, filter]);

  // Özet
  const summary = useMemo(() => {
    const total = allLeaves.length;
    const used = allLeaves.filter(l => l.isUsed).length;
    const unused = total - used;
    return { total, used, unused };
  }, [allLeaves]);

  // Para formatı
  const formatCurrency = (amount: number, currency: string = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Tarih formatı
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR');
  };

  // AccountingDataTable kolon tanımları
  const leafColumns = useMemo<DataTableColumn<LeafInfo>[]>(() => [
    {
      id: 'leafNumber',
      label: 'Yaprak No',
      field: 'leafNumber',
      sortable: true,
      align: 'center',
      render: (row) => <Typography variant="body2" fontWeight="bold">{row.leafNumber}</Typography>,
    },
    {
      id: 'isUsed',
      label: 'Durum',
      align: 'center',
      sortable: true,
      render: (row) => row.isUsed
        ? <Chip icon={<CheckCircleIcon />} label="Kullanıldı" size="small" color="warning" variant="outlined" />
        : <Chip icon={<RadioButtonUncheckedIcon />} label="Boş" size="small" color="success" variant="outlined" />,
    },
    {
      id: 'accountName',
      label: 'Cari Hesap',
      sortable: true,
      render: (row) => <Typography variant="body2">{row.checkNote?.accountName || '-'}</Typography>,
    },
    {
      id: 'amount',
      label: 'Tutar',
      align: 'right',
      sortable: true,
      render: (row) => row.checkNote
        ? <Typography variant="body2" fontWeight="bold">{formatCurrency(row.checkNote.amount, row.checkNote.currency)}</Typography>
        : <Typography variant="body2" color="text.secondary">-</Typography>,
    },
    {
      id: 'dueDate',
      label: 'Vade Tarihi',
      align: 'center',
      sortable: true,
      render: (row) => <Typography variant="body2">{row.checkNote ? formatDate(row.checkNote.dueDate) : '-'}</Typography>,
    },
    {
      id: 'status',
      label: 'Çek Durumu',
      align: 'center',
      sortable: true,
      render: (row) => row.checkNote
        ? <Chip label={statusConfig[row.checkNote.status]?.label || row.checkNote.status} size="small" color={statusConfig[row.checkNote.status]?.color || 'default'} />
        : <Typography variant="body2" color="text.secondary">-</Typography>,
    },
    {
      id: 'documentNumber',
      label: 'Belge No',
      sortable: true,
      render: (row) => <Typography variant="body2" color="text.secondary">{row.checkNote?.documentNumber || '-'}</Typography>,
    },
  ], []);

  // Koşullu satır renklendirme
  const getLeafRowSx = (row: LeafInfo) => ({
    backgroundColor: row.isUsed ? 'rgba(255, 167, 38, 0.04)' : 'rgba(76, 175, 80, 0.04)',
    cursor: row.isUsed ? 'pointer' : 'default',
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Başlık */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Tooltip title="Geri Dön">
            <IconButton onClick={() => navigate('/checkbooks')}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <MenuBookIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {checkBook?.serialNumber || 'Çek Defteri'} - Yapraklar
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <AccountBalanceIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {checkBook?.subAccount
                  ? `${checkBook.bankAccount?.bankName || ''} - ${checkBook.subAccount.accountName}`
                  : checkBook?.bankAccount?.bankName || '-'}
                {' | '}
                Numara Aralığı: {checkBook?.startNumber} - {checkBook?.endNumber}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          disabled={loading}
        >
          Yenile
        </Button>
      </Box>

      {/* Hata */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Özet Kartları */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Toplam Yaprak</Typography>
              <Typography variant="h4" fontWeight="bold">{summary.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Kullanılan</Typography>
              <Typography variant="h4" fontWeight="bold" color="warning.main">{summary.used}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Kalan</Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">{summary.unused}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtre */}
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_e, val) => { if (val) setFilter(val); }}
          size="small"
        >
          <ToggleButton value="all">
            Tümü ({summary.total})
          </ToggleButton>
          <ToggleButton value="used">
            Kullanılan ({summary.used})
          </ToggleButton>
          <ToggleButton value="unused">
            Boş ({summary.unused})
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Tablo */}
      <AccountingDataTable<LeafInfo>
        columns={leafColumns}
        rows={filteredLeaves}
        getRowId={(row) => String(row.leafNumber)}
        loading={false}
        pagination={false}
        defaultSortField="leafNumber"
        defaultSortDirection="asc"
        getRowSx={getLeafRowSx}
        emptyMessage={filter === 'used' ? 'Kullanılmış yaprak yok' : filter === 'unused' ? 'Boş yaprak yok' : 'Yaprak bulunamadı'}
        emptyIcon={<MenuBookIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
        ariaLabel="çek defteri yaprakları tablosu"
        columnResizeStorageKey="check-book-leaves"
      />
    </Box>
  );
};

export default CheckBookLeaves;
